/**
 * Cadastros: tudo que a gerência muda sem precisar de programador.
 * Cada seção fica num arquivo em telas/cadastros/:
 * 1. Representantes.tsx: a equipe (novo, editar, foto, "saiu da equipe" em
 *    TrocaDeRepresentante.tsx, excluir cadastro errado).
 * 2. Codigos.tsx: vendedores sem cadastro no mês; códigos do Winthor e nomes no
 *    SAP (de quem é cada um, e desde quando).
 * 3. Marcas.tsx: marcas (nova, nome, de onde vem o realizado, ativa, ordem).
 * 4. Feriados.tsx: feriados municipais e dias sem expediente.
 * 5. Logo.tsx: a logo do topo e das imagens para o WhatsApp.
 * Toda mudança volta com os cadastros inteiros (a tela atualiza na hora) e
 * fica anotada no histórico de alterações (tela Sistema).
 *
 * Lê: os cadastros (App.tsx) e, para os vendedores sem cadastro, as vendas do mês.
 * Usado por: App.tsx (rota #/cadastros/AAAA-MM).
 */
import { useState } from 'react';
import { Aviso, ErroNaTela, SeletorMes } from '../componentes/basicos.tsx';
import { ErroApi } from '../dados/api.ts';
import type { Cadastros as TipoCadastros, Mes } from '../dominio/tipos.ts';
import { irPara } from '../rotas.ts';
import { CodigosDaFonte, SemCadastro } from './cadastros/Codigos.tsx';
import type { Executar } from './cadastros/comum.ts';
import { Feriados } from './cadastros/Feriados.tsx';
import { Logo } from './cadastros/Logo.tsx';
import { ListaDeMarcas } from './cadastros/Marcas.tsx';
import { ListaDeRepresentantes } from './cadastros/Representantes.tsx';

interface Props {
  mes: Mes;
  cadastros: TipoCadastros;
  aoAtualizar: (cadastros: TipoCadastros) => void;
}

export function Cadastros({ mes, cadastros, aoAtualizar }: Props) {
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const executar: Executar = async (acao, sucesso) => {
    setErro(null);
    setAviso(null);
    try {
      aoAtualizar(await acao());
      setAviso(sucesso);
      return true;
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
      return false;
    }
  };

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <h1>Cadastros</h1>
          <p>Representantes (e fotos), códigos de vendedor, marcas, feriados e logo.</p>
        </div>
        <SeletorMes mes={mes} aoMudar={(novo) => irPara({ tela: 'cadastros', mes: novo })} />
      </div>
      {/* Mensagens ficam grudadas no topo da tela: a seção que mudou pode estar lá embaixo. */}
      <div className="mensagens-fixas" aria-live="polite">
        {erro ? <ErroNaTela erro={erro} /> : null}
        {aviso ? <Aviso tipo="ok">{aviso}</Aviso> : null}
      </div>
      <ListaDeRepresentantes mes={mes} cadastros={cadastros} executar={executar} />
      <SemCadastro mes={mes} cadastros={cadastros} executar={executar} />
      <CodigosDaFonte fonte="winthor" mes={mes} cadastros={cadastros} executar={executar} />
      <CodigosDaFonte fonte="sap-bauny" mes={mes} cadastros={cadastros} executar={executar} />
      <ListaDeMarcas marcas={cadastros.marcas} executar={executar} />
      <Feriados feriados={cadastros.feriados ?? []} executar={executar} />
      <Logo logo={cadastros.logo ?? null} executar={executar} />
    </div>
  );
}
